/* eslint-disable max-len */
// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { RequiredNs as ComponentNs } from '../../components/localized/types'

// External imports
import Link from 'next/link'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'react-i18next'

import styled from '@emotion/styled'

// Others
import Footer from '../../components/localized/Footer'

import { Title, Description } from '../../components/Hero'
import LogoHomeLink from '../../components/LogoHomeLink'

import { defaultPageSpecificSEO, MOBILE_WIDTH_PX, PRIVACY_PATHNAME, TABLET_WIDTH_PX, TERMS_PATHNAME } from '../../constants'

/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'terms-referrals' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, 'terms-referrals': true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

/**
 * Constants & Styles
 */

const Wrapper = styled.div`
  width: 100%;
  max-width: 940px;
  margin: 0 auto;
  padding: 96px 64px 128px;
  @media screen and (max-width: ${TABLET_WIDTH_PX}px) {
    width: 80%;
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 80px;
  }

  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    padding-top: 48px;
    padding-bottom: 64px;
  }
`

const Root: NextPage = () => {
  const router = useRouter()

  const { t } = useTranslation('terms-referrals')
  const { t: tCommon } = useTranslation('common')

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />

      <Wrapper>
        <LogoHomeLink />
        <Title>Referral Terms and Conditions</Title>
        <Description>Last Updated on March 5, 2021</Description>

        <p>
        By participating in our Invite a Friend program (as a &quot;Referrer&quot;), you are subject to <Link href={TERMS_PATHNAME}>Outwrite&apos;s Terms of Service</Link> and <Link href={PRIVACY_PATHNAME}>Privacy Policy</Link>, as well as the following additional Terms & Conditions:
        </p>

        <ul role="list">
          <li>Eligibility. The Invite a Friend program is available to individuals only.</li>
          <li>Referred User. The Referred User cannot be the same person as the Referrer. They must also be a new user, or a current user who has never trialled or subscribed to Outwrite Pro or Outwrite for Teams before.</li>
          <li>Referral Rewards. To earn a reward as a Referrer, the Referred User must purchase an Outwrite Pro subscription using the Referrer&apos;s personal referral link. Once a purchase is made, the reward will be applied to the Referrer&apos;s account within 30 days. This reward can only be used for future subscription payments. It cannot be exchanged, transferred, or refunded for proper cash. Outwrite may withdraw the Referral Reward if the Referred User disputes or receives a refund for their subscription payment.</li>
          <li>Spam. Any distribution of your personal referral link must comply with relevant anti-spam laws and regulations.</li>
          <li>Right to Change Terms. Outwrite reserves the right to change the terms and conditions of the Invite a Friend program at any time.</li>
          <li>Right to Cancel Program. Outwrite reserves the right to cancel the Invite a Friend program at any time.</li>
          <li>Outwrite reserves the right to remove users from the Invite a Friend program if they do not comply with these Terms or abuse the program.</li>
        </ul>

        <p>
          If you have any questions about these Terms and Conditions, you can contact us at <a href='mailto:help@outwrite.com'>help@outwrite.com</a>
        </p>
      </Wrapper>

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default Root
